import { useState } from 'react';
import Layout from '@/components/feature/Layout';
import { contactInfo } from '@/mocks/codesajan';

export default function ContactPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('success');
  };

  return (
    <Layout>
      {/* Hero header with image background */}
      <div className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=simple%20community%20water%20committee%20office%20building%20in%20rural%20bolivia%2C%20clean%20modest%20exterior%20with%20small%20signage%2C%20bright%20daylight%2C%20warm%20natural%20lighting%2C%20editorial%20documentary%20photography%2C%20clean%20composition%20with%20space%20for%20text%20overlay&width=1800&height=600&seq=contact-hero-office&orientation=landscape"
            alt="Oficinas de CODESAJAN en San Jacinto Norte"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60" />
        </div>
        <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto text-center">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-300 mb-3">
            Contacto
          </span>
          <h1 className="font-heading font-bold text-3xl md:text-5xl text-white mb-4">
            Contactanos
          </h1>
          <p className="text-white/70 text-sm md:text-base max-w-2xl mx-auto">
            Estamos aquí para atenderte. Escríbenos o visítanos en nuestras oficinas.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="w-full px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-12">
          {/* Contact info */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <h2 className="font-heading font-bold text-xl text-foreground-950 mb-4">
                Información de Contacto
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                    <i className="ri-map-pin-line text-primary-600 w-4 h-4 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">Dirección</p>
                    <p className="text-sm text-foreground-600">{contactInfo.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center shrink-0">
                    <i className="ri-phone-line text-accent-600 w-4 h-4 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">Teléfono</p>
                    <p className="text-sm text-foreground-600">{contactInfo.phoneMobile}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-secondary-50 flex items-center justify-center shrink-0">
                    <i className="ri-mail-line text-secondary-600 w-4 h-4 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">Email</p>
                    <p className="text-sm text-foreground-600">{contactInfo.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                    <i className="ri-time-line text-primary-600 w-4 h-4 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">Horario de Atención</p>
                    <p className="text-sm text-foreground-600">{contactInfo.hours}</p>
                    <p className="text-sm text-foreground-600">{contactInfo.weekend}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Social - Only WhatsApp */}
            <div>
              <p className="text-sm font-medium text-foreground-950 mb-3">Contacto Rápido</p>
              <div className="flex items-center gap-3">
                <a
                  href={contactInfo.social.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-green-50 hover:bg-green-100 flex items-center justify-center transition-colors"
                  aria-label="WhatsApp"
                >
                  <i className="ri-whatsapp-line text-green-600 w-5 h-5 flex items-center justify-center"></i>
                </a>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            <div className="p-6 md:p-8 rounded-xl bg-background-50 border border-background-200/70">
              <h2 className="font-heading font-bold text-xl text-foreground-950 mb-6">
                Enviar Mensaje
              </h2>
              {formStatus === 'success' ? (
                <div className="p-6 rounded-lg bg-green-50 border border-green-200 text-center">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-check-line text-green-600 w-6 h-6 flex items-center justify-center"></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-950 mb-1">Mensaje Enviado</h3>
                  <p className="text-sm text-foreground-600">Gracias por contactarnos. Te responderemos a la brevedad.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">Nombre completo</label>
                      <input
                        type="text"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all"
                        placeholder="Tu nombre"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">Teléfono</label>
                      <input
                        type="tel"
                        name="phone"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all"
                        placeholder="Tu teléfono"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">Email</label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all"
                      placeholder="tu@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">Asunto</label>
                    <select
                      name="subject"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all"
                    >
                      <option value="consulta">Consulta general</option>
                      <option value="reclamo">Reclamo de servicio</option>
                      <option value="pago">Consulta de pago</option>
                      <option value="titular">Cambio de titular</option>
                      <option value="otro">Otro</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">Mensaje</label>
                    <textarea
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all resize-none"
                      placeholder="Escribe tu mensaje..."
                    />
                    <p className="text-xs text-foreground-500 mt-1">Máximo 500 caracteres</p>
                  </div>
                  {/* Honeypot */}
                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="hp-field"
                  />
                  <button
                    type="submit"
                    className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-colors text-sm"
                  >
                    Enviar Mensaje
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}